import { ArrowSquareOutIcon, InfoIcon, MapPinIcon, StorefrontIcon } from '@phosphor-icons/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { useReportListingOpened } from '@entities/search-run';
import type { VehicleSeller } from '@entities/vehicle/types';
import { ButtonLink } from '@shared/ui';

import styles from './VehicleDealer.module.css';

interface VehicleDealerProps {
  runId: string;
  vehicleId: string;
  seller: VehicleSeller | null;
  /** Объявление на сайте автохауса (`http`/`https`); `null` — ссылки нет. */
  listingUrl: string | null;
  /**
   * Строка «Diese Händlerdaten sind für Sie kostenlos verfügbar…» под блоком —
   * только до оплаты, где она объясняет, что бесплатно (FE-587). После оплаты
   * переписку с автохаусом ведёт Karl, и приглашение «связаться напрямую» там
   * сбивало бы с того, за что заплачено.
   */
  showFreeNote: boolean;
}

/**
 * Блок автохауса на экране автомобиля (Epic 58, FE-587, макет 04).
 *
 * **Только имя, город и ссылка на объявление.** Телефона в схеме нет, адрес
 * точки не заполнен, e-mail не отдаётся ни на одном уровне — поэтому
 * «Anrufen», «E-Mail schreiben», карта и маршрут из макета не рисуются
 * (DESIGN_SYSTEM, врезка «Из макетов не переносится»). Никаких оценок
 * автохауса здесь нет и быть не может: ни звёзд, ни «geprüfter Händler»
 * (CLAUDE.md §2).
 *
 * «Website besuchen» — обычная ссылка в новой вкладке: браузер открывает её
 * сам и сразу, а событие `source_listing_opened` уходит параллельно и переход
 * не задерживает (BE-582). Без ссылки кнопки нет, текст блока остаётся.
 */
export function VehicleDealer({
  runId,
  vehicleId,
  seller,
  listingUrl,
  showFreeNote,
}: VehicleDealerProps) {
  const { t } = useTranslation();
  const reportListingOpened = useReportListingOpened();
  const titleId = useId();
  const noData = t('catalog.noData');

  return (
    <div className={styles.wrap}>
      <section className={styles.card} aria-labelledby={titleId}>
        <span className={styles.icon} aria-hidden="true">
          <StorefrontIcon size={24} />
        </span>
        <div className={styles.body}>
          <h2 id={titleId} className={styles.name}>
            {seller?.name ?? noData}
          </h2>
          <p className={styles.city}>
            <MapPinIcon size={16} aria-hidden="true" />
            <span className="visually-hidden">{t('catalog.spec.location')}: </span>
            {seller?.city ?? noData}
          </p>
        </div>
        {listingUrl === null ? null : (
          <ButtonLink
            to={listingUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="outline-action"
            className={styles.website}
            iconEnd={<ArrowSquareOutIcon size={16} aria-hidden="true" />}
            onClick={() => {
              reportListingOpened({ runId, vehicleId, via: 'dealer_block' });
            }}
          >
            {t('showcase.dealer.website')}
            <span className="visually-hidden"> {t('showcase.dealer.newTab')}</span>
          </ButtonLink>
        )}
      </section>

      {showFreeNote ? (
        <p className={styles.note}>
          <InfoIcon size={20} weight="fill" aria-hidden="true" className={styles.noteIcon} />
          <span>
            <strong className={styles.noteTitle}>{t('showcase.dealer.free')}</strong>{' '}
            {t('showcase.dealer.freeText')}
          </span>
        </p>
      ) : null}
    </div>
  );
}
